// A small, self-spinning ring-system preview — a faithful mini of the ASTRA
// disc: five concentric S/T/A/G/E rings (ORDER's chronological layer), five
// nodes per spoke (one per ring), one spoke per active spoke (SHAPE sector or
// ORDER gear), evenly spaced and slowly rotating. Used in the ORACLE tool so
// the user sees the disc gain/lose spokes as they toggle spokes on/off or add
// a custom one, for either system (ORDER always-on / SHAPE once unlocked).
import React, { useEffect, useRef, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import Svg, { Circle, Ellipse, Line, Text as SvgText } from 'react-native-svg';
import { useTheme, makeThemedStyles, DISPLAY_FONT, radii } from '../v10/theme';
import type { Spoke } from './sectorConfig';

const SIZE = 270;
const CX = SIZE / 2;
const CY = SIZE / 2;
const RX_OUTER = SIZE * 0.36; // outermost ring radius
const TILT = 0.42; // ry / rx — reads as a tilted disc
const RX_RATIO = [0.2, 0.4, 0.6, 0.8, 1.0]; // inner→outer, 5 stage rings
const RING_SHADE = [0.45, 0.25, 0.05, -0.15, -0.35]; // inner lightest → outer darkest
const NODE_R = 4.5;
const SPIN_SECONDS = 20; // one full rotation

const rad = (deg: number) => (deg * Math.PI) / 180;
/** Evenly-spaced spokes, first at 6 o'clock (90°). */
const angleFor = (i: number, count: number) => 90 + (360 / count) * i;
const pt = (rx: number, ry: number, deg: number) => ({ x: CX + rx * Math.cos(rad(deg)), y: CY + ry * Math.sin(rad(deg)) });

/** Lighten (amt>0) / darken (amt<0) a #rrggbb hex — per-ring node shading. */
function shade(hex: string, amt: number): string {
  const n = parseInt(hex.slice(1), 16);
  let r = (n >> 16) & 255;
  let g = (n >> 8) & 255;
  let b = n & 255;
  if (amt >= 0) {
    r += (255 - r) * amt;
    g += (255 - g) * amt;
    b += (255 - b) * amt;
  } else {
    r *= 1 + amt;
    g *= 1 + amt;
    b *= 1 + amt;
  }
  const to = (v: number) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0');
  return `#${to(r)}${to(g)}${to(b)}`;
}

export function SectorCompassPreview({ spokes }: { spokes: Spoke[] }) {
  const styles = useStyles();
  const { t, c } = useTheme();
  const count = spokes.length;
  const [rotation, setRotation] = useState(0);
  const rafRef = useRef<number | null>(null);

  // Constant idle spin — recompute positions each frame so the labels stay
  // upright while they orbit (matches the live ASTRA).
  useEffect(() => {
    const DEG_PER_MS = 360 / (SPIN_SECONDS * 1000);
    let last = Date.now();
    const tick = () => {
      const now = Date.now();
      setRotation((r) => (r + (now - last) * DEG_PER_MS) % 360);
      last = now;
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <View style={styles.wrap}>
      <Svg width={SIZE} height={SIZE}>
        {/* stage rings */}
        {RX_RATIO.map((ratio, ri) => (
          <Ellipse
            key={`ring-${ri}`}
            cx={CX}
            cy={CY}
            rx={RX_OUTER * ratio}
            ry={RX_OUTER * ratio * TILT}
            stroke={c.baseDark}
            strokeWidth={0.7}
            strokeOpacity={0.5}
            fill="none"
          />
        ))}
        {spokes.map((spoke, i) => {
          const deg = angleFor(i, count) + rotation;
          const outer = pt(RX_OUTER, RX_OUTER * TILT, deg);
          const label = pt(RX_OUTER + 16, RX_OUTER * TILT + 16, deg);
          return (
            <React.Fragment key={spoke.id}>
              {/* spoke line from centre to outer ring */}
              <Line x1={CX} y1={CY} x2={outer.x} y2={outer.y} stroke={spoke.color} strokeWidth={1} strokeOpacity={0.4} />
              {/* one node per stage ring */}
              {RX_RATIO.map((ratio, ri) => {
                const p = pt(RX_OUTER * ratio, RX_OUTER * ratio * TILT, deg);
                const back = Math.sin(rad(deg)) < 0;
                return (
                  <Circle
                    key={`${spoke.id}-${ri}`}
                    cx={p.x}
                    cy={p.y}
                    r={NODE_R * (back ? 0.82 : 1)}
                    fill={shade(spoke.color, RING_SHADE[ri])}
                    stroke={c.white}
                    strokeWidth={1}
                    opacity={back ? 0.75 : 1}
                  />
                );
              })}
              <SvgText
                x={label.x}
                y={label.y + 3}
                fontSize={9}
                fontWeight="800"
                fill={spoke.color}
                textAnchor="middle"
              >
                {spoke.label.toUpperCase()}
              </SvgText>
            </React.Fragment>
          );
        })}
        {/* centre hub */}
        <Circle cx={CX} cy={CY} r={SIZE * 0.045} fill={t.navyRaised} stroke={c.primary} strokeWidth={1.5} />
      </Svg>
      <Text style={styles.caption}>
        {count} spoke{count === 1 ? '' : 's'} · live ASTRA disc
      </Text>
    </View>
  );
}

const useStyles = makeThemedStyles(({ t, c, neu }) =>
  StyleSheet.create({
  wrap: { alignItems: 'center' },
  caption: { fontSize: 10.5, color: c.slate, fontStyle: 'italic', marginTop: 2, textAlign: 'center' },
}),
);
